import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { axiosClient } from '../../../core/axios-client'

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

const removeUndefinedDeep = (value) => {
  if (Array.isArray(value)) {
    return value.map(removeUndefinedDeep).filter((v) => v !== undefined)
  }

  if (isPlainObject(value)) {
    const out = {}
    Object.keys(value).forEach((key) => {
      const nextValue = removeUndefinedDeep(value[key])
      if (nextValue !== undefined) out[key] = nextValue
    })
    return out
  }

  return value === undefined ? undefined : value
}

const stripEmptyObjects = (payload) => {
  if (!isPlainObject(payload)) return payload

  const out = { ...payload }

  if (isPlainObject(out.user) && Object.keys(out.user).length === 0) {
    delete out.user
  }

  if (
    isPlainObject(out.individual_entrepreneur) &&
    Object.keys(out.individual_entrepreneur).length === 0
  ) {
    delete out.individual_entrepreneur
  }

  return out
}

const fetchCouriers = createAsyncThunk(
  'couriers/fetchCouriers',
  async ({ filters = {}, company_id } = {}) => {
    const response = await axiosClient.get('/couriers/', {
      params: {
        ...filters,
        ...(company_id ? { company_id } : {}),
      },
    })
    return response.data
  }
)

const fetchCouriersCount = createAsyncThunk('fetchCouriersCount', async () => {
  const response = await axiosClient.get('/couriers/')
  return response.data.count
})

const fetchCourier = createAsyncThunk('couriers/fetchCourier', async ({ id }) => {
  const response = await axiosClient.get(`/couriers/${id}`)
  return response.data
})

const updateCourier = createAsyncThunk('couriers/updateCourier', async (payload = {}) => {
  const { id, ...patch } = payload || {}
  const requestData = stripEmptyObjects(removeUndefinedDeep(patch))
  const response = await axiosClient.patch(`/couriers/${id}`, requestData)
  return response.data
})

const deleteCourier = createAsyncThunk('couriers/deleteCourier', async ({ id }) => {
  const response = await axiosClient.delete(`/couriers/${id}`)
  return response.data
})

const rebindYandex = createAsyncThunk(
  'couriers/rebindYandex',
  async ({ courier_id, yandex_driver_id }) => {
    const response = await axiosClient.post('/couriers/rebind-yandex/', {
      courier_id,
      yandex_driver_id,
    })
    return response.data
  }
)

const fetchDeliveryIntegrations = createAsyncThunk(
  'couriers/fetchDeliveryIntegrations',
  async ({ companyId } = {}, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/companies/${companyId}/integrations/delivery`)
      return { companyId: String(companyId || ''), items: response.data }
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        'Не удалось загрузить интеграции'

      return rejectWithValue({ companyId: String(companyId || ''), message })
    }
  }
)

const fetchExternalDrivers = createAsyncThunk(
  'couriers/fetchExternalDrivers',
  async (
    { companyId, integrationId, requestIntegrationId, filters = {} } = {},
    { rejectWithValue }
  ) => {
    const companyKey = String(companyId || '')
    if (!companyKey) {
      return rejectWithValue({
        companyId: companyKey,
        integrationId,
        message: 'companyId is required',
      })
    }

    const apiIntegrationId = requestIntegrationId ?? integrationId

    try {
      const response = await axiosClient.get(
        `/companies/${companyKey}/integrations/delivery/${apiIntegrationId}/external-drivers`,
        { params: { ...filters } }
      )

      return { companyId: companyKey, integrationId, payload: response.data }
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        'Не удалось загрузить внешних курьеров'

      return rejectWithValue({ companyId: companyKey, integrationId, message })
    }
  }
)

const fetchExternalDriver = createAsyncThunk(
  'couriers/fetchExternalDriver',
  async ({ companyId, integrationId, driverId } = {}, { rejectWithValue, getState }) => {
    const state = getState()

    const resolvedCompanyId =
      companyId ?? state?.companies?.selected_company?.id ?? state?.companies?.selected_company_id

    const companyKey = String(resolvedCompanyId || '')
    if (!companyKey) {
      return rejectWithValue({ companyId: companyKey, message: 'companyId is required' })
    }

    if (!integrationId || !driverId) {
      return rejectWithValue({
        companyId: companyKey,
        message: 'integrationId and driverId are required',
      })
    }

    try {
      const response = await axiosClient.get(
        `/companies/${companyKey}/integrations/delivery/${integrationId}/external-drivers/${driverId}`
      )
      return response.data
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        'Не удалось загрузить курьера из интеграции'

      return rejectWithValue({ companyId: companyKey, message })
    }
  }
)

const initialState = {
  listItems: {
    items: [],
    page: 1,
    page_size: 10,
    count: 0,
  },
  listItemsRequestId: null,
  couriers: [],
  courier: null,
  couriersCount: 0,

  deliveryIntegrations: [],
  deliveryIntegrationsLoading: false,
  deliveryIntegrationsStatus: 'idle',
  deliveryIntegrationsError: null,
  deliveryIntegrationsCompanyId: null,

  externalDriversListItems: {
    items: [],
    page: 1,
    page_size: 10,
    count: 0,
    integration_id: null,
  },
  externalDriversLoading: false,

  externalDriver: null,
  externalDriverLoading: false,
}

export const slice = createSlice({
  name: 'couriers',
  initialState,
  reducers: {
    setListItems(state, { payload }) {
      state.listItems = {
        ...state.listItems,
        ...payload,
      }
      state.couriers = payload.items
    },
    resetListItems(state) {
      state.listItems = { items: [], page: 1, page_size: 10, count: 0 }
      state.couriers = []
    },

    setExternalDriversListItems(state, { payload }) {
      state.externalDriversListItems = {
        ...state.externalDriversListItems,
        ...payload,
      }
    },
    resetExternalDriversListItems(state) {
      state.externalDriversListItems = {
        items: [],
        page: 1,
        page_size: 10,
        count: 0,
        integration_id: null,
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCouriers.pending, (state, action) => {
      state.listItemsRequestId = action.meta.requestId
    })

    builder.addCase(fetchCouriers.fulfilled, (state, { payload, meta }) => {
      // Ignore stale responses from an earlier, superseded fetchCouriers call
      // (e.g. an initial fetch fired before selectedCompanyId resolved to its real value).
      if (meta.requestId !== state.listItemsRequestId) return

      const { count = 0, results = [] } = payload || {}
      state.listItems = {
        ...state.listItems,
        items: results,
        count,
      }
      state.couriers = results
    })

    builder.addCase(deleteCourier.fulfilled, (state, { payload }) => {
      state.couriers = state.couriers.filter((courier) => courier.id !== payload.id)
      state.courier = null
    })

    builder.addCase(fetchCourier.fulfilled, (state, { payload }) => {
      state.courier = payload
    })

    builder.addCase(updateCourier.fulfilled, (state, { payload }) => {
      state.courier = payload
    })

    builder.addCase(fetchCouriersCount.fulfilled, (state, { payload }) => {
      state.couriersCount = payload
    })

    builder.addCase(fetchDeliveryIntegrations.pending, (state, action) => {
      state.deliveryIntegrationsLoading = true
      state.deliveryIntegrationsStatus = 'loading'
      state.deliveryIntegrationsError = null
      state.deliveryIntegrationsCompanyId = String(action.meta?.arg?.companyId || '')
    })

    builder.addCase(fetchDeliveryIntegrations.fulfilled, (state, { payload }) => {
      state.deliveryIntegrationsLoading = false
      state.deliveryIntegrationsStatus = 'succeeded'
      state.deliveryIntegrationsError = null

      const companyId = String(payload?.companyId || '')
      state.deliveryIntegrationsCompanyId = companyId

      const raw = payload?.items
      const list = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.results)
          ? raw.results
          : Array.isArray(raw?.items)
            ? raw.items
            : []

      state.deliveryIntegrations = list
    })

    builder.addCase(fetchDeliveryIntegrations.rejected, (state, action) => {
      state.deliveryIntegrationsLoading = false
      state.deliveryIntegrationsStatus = 'failed'
      state.deliveryIntegrationsCompanyId = String(
        action.payload?.companyId || action.meta?.arg?.companyId || ''
      )
      state.deliveryIntegrationsError =
        action.payload?.message || action.error?.message || 'Не удалось загрузить интеграции'
    })

    builder.addCase(fetchExternalDrivers.pending, (state) => {
      state.externalDriversLoading = true
    })

    builder.addCase(fetchExternalDrivers.fulfilled, (state, { payload }) => {
      state.externalDriversLoading = false

      const { integrationId, payload: data } = payload || {}
      const list = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : []

      const count = typeof data?.count === 'number' ? data.count : list.length

      state.externalDriversListItems = {
        ...state.externalDriversListItems,
        items: list,
        count,
        integration_id: integrationId ?? null,
      }
    })

    builder.addCase(fetchExternalDrivers.rejected, (state) => {
      state.externalDriversLoading = false
      state.externalDriversListItems = { ...state.externalDriversListItems, items: [], count: 0 }
    })

    builder.addCase(fetchExternalDriver.pending, (state) => {
      state.externalDriverLoading = true
      state.externalDriver = null
    })

    builder.addCase(fetchExternalDriver.fulfilled, (state, { payload }) => {
      state.externalDriverLoading = false
      state.externalDriver = payload
    })

    builder.addCase(fetchExternalDriver.rejected, (state) => {
      state.externalDriverLoading = false
      state.externalDriver = null
    })
  },
})

export const {
  setListItems,
  resetListItems,

  setExternalDriversListItems,
  resetExternalDriversListItems,
} = slice.actions

export {
  fetchCouriers,
  fetchCouriersCount,
  fetchCourier,
  updateCourier,
  deleteCourier,
  rebindYandex,

  fetchDeliveryIntegrations,
  fetchExternalDrivers,
  fetchExternalDriver,
}

export default slice.reducer
